/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import { classNames } from '~/utils/client/helpers'
import { Detail, Small } from '../../text/Text'

export type CircularStepProps = React.ComponentProps<'li'> & {
  title?: string
  description?: string
  icon: React.ForwardRefExoticComponent<Omit<React.SVGProps<SVGSVGElement>, 'ref'>>
  className?: string
  isLastStep?: boolean
  isFirstStep?: boolean
  status?: 'complete' | 'current' | 'upcoming'
}

export const CircularStep: React.FC<CircularStepProps> = ({
  title,
  description,
  className,
  isLastStep = false,
  isFirstStep = false,
  status = 'upcoming',
  ...props
}) => {
  const iconStyle = classNames(status === 'current' ? 'text-brand' : 'text-secondary', 'h-5 w-5')
  const titleStyle = classNames(
    status === 'current' ? 'text-black' : 'text-secondary',
    'mb-1 text-sm md:text-base hidden md:block'
  )
  const descriptionStyle = classNames(
    status === 'current' ? 'text-secondary' : 'text-muted',
    'hidden md:block'
  )

  const positionStyle = classNames(
    isFirstStep ? 'items-start' : isLastStep ? 'items-end' : 'items-center',
    'md:items-center'
  )
  return (
    <li
      key={title}
      className={classNames('flex-1', className)}
      aria-current={status === 'current' ? 'step' : undefined}
      {...props}
    >
      <div className="flex flex-col text-center">
        <div className="relative">
          <div className={classNames('flex flex-col', positionStyle)}>
            <span className="relative z-10 flex h-10 w-10 items-center justify-center rounded-md bg-white border-2 border-solid group-hover:bg-surface-hover border-border-subtle mb-2">
              <props.icon className={iconStyle} aria-hidden="true" />
            </span>
            {title && <Detail className={titleStyle}>{title}</Detail>}
            {description && (
              <Small className={descriptionStyle} aria-hidden="true">
                {description}
              </Small>
            )}
          </div>
          {/* DESKTOP */}
          <div className="absolute inset-0 flex pt-5 left-1/2 hidden md:block" aria-hidden="true">
            <div className="h-0.5 w-full bg-border-subtle" />
          </div>
          <div className="absolute inset-0 flex pt-5 right-1/2 hidden md:block" aria-hidden="true">
            <div className="h-0.5 w-full bg-border-subtle" />
          </div>
          {isFirstStep && (
            <div
              className="h-4 w-4 absolute left-0 top-5 border border-border-subtle rounded bg-white origin-top translate-y-[-50%] hidden md:block"
              aria-hidden="true"
            />
          )}
          {isLastStep && (
            <div
              className="h-4 w-4 absolute right-0 top-5 border border-border-subtle rounded bg-white origin-top translate-y-[-50%] hidden md:block"
              aria-hidden="true"
            />
          )}
          {/* MOBILE */}
          {((!isLastStep && !isFirstStep) || isLastStep) && (
            <div className="absolute inset-0 flex pt-5 block md:hidden" aria-hidden="true">
              <div className="h-0.5 w-full bg-border-subtle" />
            </div>
          )}
          {((!isLastStep && !isFirstStep) || !isLastStep) && (
            <div className="absolute inset-0 flex pt-5 block md:hidden" aria-hidden="true">
              <div className="h-0.5 w-full bg-border-subtle" />
            </div>
          )}
        </div>
      </div>
    </li>
  )
}
